import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import cx from "classnames";
import styles from "../scss/DigitalAdvice.module.scss";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import DigitalAdviceQuestion from "../components/DigitalAdviceQuestion";
import DigitalAdviceQuestionFrame from "../components/DigitalAdviceQuestionFrame";
import ProgressBar from "../../common/components/ProgressBar/ProgressBar";
import { FeatureFlag } from "../../compare/enums/FeatureFlags";
import { IQuestionOptionImage } from "../interfaces/IDigitalAdviceData";

type QuestionPageParams = {
    id: string;
};

const DigitalAdviceProcessPage: React.FC = () => {
    const params = useParams<QuestionPageParams>();
    const questionId = params.id;

    const frameRef = useRef(null);
    const [baseImages, setBaseImages] = useState([]);
    const [frameLabel, setFrameLabel] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const [progressBarCurrentStage, setProgressBarCurrentStage] = useState<
        React.ComponentProps<typeof ProgressBar>["progressBarCurrentStage"]
    >("why");

    const progressBarStages: React.ComponentProps<
        typeof ProgressBar
    >["progressBarStages"] = [
        {
            sectionTitle: "Why care?",
            sectionTitleMobile: "Why",
            id: "why"
        },
        {
            sectionTitle: "You & your family",
            sectionTitleMobile: "You",
            id: "you"
        },
        {
            sectionTitle: "Your finances",
            sectionTitleMobile: "Finances",
            id: "finances"
        },
        {
            sectionTitle: "Our Advice",
            sectionTitleMobile: "Advice",
            id: "advice"
        }
    ];

    const handleFrameImages = (image, isAdded: boolean = false): void => {
        frameRef.current.addImages(image, isAdded);
    };

    const handleMultipleFrameImages = (images, isAdded: boolean = false): void => {
        frameRef.current.addMultipleImages(images, isAdded);
    };

    const handleFrameIMGPlaceholder = (images): void => {
        frameRef.current.setImagePlaceholders(images);
    };

    const handleResetPlaceholder = (): void => {
        frameRef.current.resetPlaceholder();
    };

    const handleSetFrameImages = (imagesList): void => {
        frameRef.current.setImagesList(imagesList);
    };

    const getFrameImages = (): Array<IQuestionOptionImage> => {
        return frameRef.current.getFrameImages();
    };

    const resetFrameImages = (): void => {
        frameRef.current.resetFrameImages();
    };

    return (
        <React.Fragment>
            <ProgressBar
                progressBarCurrentStage={progressBarCurrentStage}
                progressBarStages={progressBarStages}
            />
            <div className={cx(styles["advice-progress"], styles["layout"])}>
                <div className={styles.col}>
                    <DigitalAdviceQuestion
                        questionId={questionId}
                        setProgressBarCurrentStage={setProgressBarCurrentStage}
                        addFrameImage={handleFrameImages}
                        addMultipleFrameImages={handleMultipleFrameImages}
                        setBaseImages={setBaseImages}
                        setImagePlaceholders={handleFrameIMGPlaceholder}
                        resetFramePlaceholder={handleResetPlaceholder}
                        setIsLoading={setIsLoading}
                        setFrameLabel={setFrameLabel}
                        setFrameImages={handleSetFrameImages}
                        getFrameImages={getFrameImages}
                        resetFrameImages={resetFrameImages}
                    />
                </div>
                <div className={cx(styles.col, styles.spaceTop)}>
                    <DigitalAdviceQuestionFrame
                        ref={frameRef}
                        images={baseImages}
                        label={frameLabel}
                        hidePreload={!isLoading}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default DigitalAdviceProcessPage;
